import React, { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import { TendbeQRCodeBorder } from "./TendbeQRCode.styles";
import { QRCode } from "react-qrcode-logo";
import { AccountTypes } from "@Interfaces/Entities/User";
import useRestrictions from "@Hooks/useRestrictions";
import { theme } from "@Styled";

export const TENDBE_QR_LOGO = "/images/tb-qr-logo.svg";
const tendBeLogo = TENDBE_QR_LOGO;

const tbBlue = theme.palette.common.tbBlue;

export type TendbeQRCodeHandle = {
    download: (fileType?: "png" | "jpg" | "webp", fileName?: string) => void;
};

type TendbeQRCodeProps = {
    value: string;
    profileImgPath: string;
    qrColor: string | undefined;
    qrCodeHeight?: number;
    qrCodeWidth?: number;
    qrSize?: number;
    pixelRatio?: number;
    useChannelProfileAsQrLogo: boolean | undefined;
    /**
     * Logo decided by the caller instead of the logged-in account's plan. Needed where
     * nobody is logged in, such as the Present Mode screen (the server picks the logo).
     */
    logoImageOverride?: string;
};

type EyeRadiusConfig = {
    outer: [number, number, number, number];
    inner: [number, number, number, number];
};

// Static radius config keeps the locator "eyes" perfectly round regardless of QR size.
const ROUNDED_EYES: [EyeRadiusConfig, EyeRadiusConfig, EyeRadiusConfig] = [
    {
        outer: [100, 100, 100, 100],
        inner: [100, 100, 100, 100],
    },
    {
        outer: [100, 100, 100, 100],
        inner: [100, 100, 100, 100],
    },
    {
        outer: [100, 100, 100, 100],
        inner: [100, 100, 100, 100],
    },
];

export const TendbeQRCode = forwardRef<TendbeQRCodeHandle, TendbeQRCodeProps>(
    (
        {
            value,
            profileImgPath,
            qrColor,
            qrCodeHeight = 50,
            qrCodeWidth = 50,
            qrSize = 175,
            pixelRatio = 4,
            useChannelProfileAsQrLogo = false,
            logoImageOverride,
        },
        ref
    ) => {
        // Keep the canvas high-res but retain the existing API by sanitizing pixelRatio here.
        const scaledPixelRatio = useMemo(() => {
            if (Number.isNaN(pixelRatio) || pixelRatio <= 0) {
                return 1;
            }
            return pixelRatio;
        }, [pixelRatio]);

        const canvasSize = useMemo(
            () => Math.round(qrSize * scaledPixelRatio),
            [qrSize, scaledPixelRatio]
        );

        const scaledLogoHeight = useMemo(
            () => Math.round(qrCodeHeight * scaledPixelRatio),
            [qrCodeHeight, scaledPixelRatio]
        );

        const scaledLogoWidth = useMemo(
            () => Math.round(qrCodeWidth * scaledPixelRatio),
            [qrCodeWidth, scaledPixelRatio]
        );

        const containerRef = useRef<HTMLDivElement | null>(null);

        useImperativeHandle(
            ref,
            () => ({
                download: (fileType = "png", fileName) => {
                    const canvas =
                        containerRef.current?.querySelector("canvas");
                    if (!canvas || !(canvas instanceof HTMLCanvasElement)) {
                        return;
                    }
                    const normalizedType =
                        fileType === "jpg" ? "jpeg" : fileType;
                    const extension = fileType ?? "png";
                    try {
                        // Export the rendered bitmap directly from the canvas to avoid depending on library helpers.
                        const dataUrl = canvas.toDataURL(
                            `image/${normalizedType}`
                        );
                        const link = document.createElement("a");
                        link.href = dataUrl;
                        link.download = fileName || `tendbe-qr.${extension}`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(
                            "Failed to download QR code canvas.",
                            error
                        );
                    }
                },
            }),
            []
        );

        const [accountType] = useRestrictions();

        const defaultPremiumImage = useMemo(
            () =>
                useChannelProfileAsQrLogo && profileImgPath
                    ? profileImgPath
                    : tendBeLogo,
            [profileImgPath, useChannelProfileAsQrLogo]
        );

        return (
            <TendbeQRCodeBorder
                ref={containerRef}
                color={qrColor || tbBlue}
                displaySize={qrSize}
            >
                <QRCode
                    qrStyle="dots"
                    logoImage={
                        logoImageOverride ??
                        (accountType === AccountTypes.Freemium
                            ? tendBeLogo
                            : defaultPremiumImage)
                    }
                    logoHeight={scaledLogoHeight}
                    logoWidth={scaledLogoWidth}
                    eyeRadius={ROUNDED_EYES}
                    value={value}
                    logoOpacity={1}
                    size={canvasSize}
                    fgColor={qrColor || tbBlue}
                    enableCORS
                />
            </TendbeQRCodeBorder>
        );
    }
);

TendbeQRCode.displayName = "TendbeQRCode";
